// apps/client/src/pages/broker-activity/OverviewPage.tsx
//
// Phase E observability landing — Broker Activity subapp overview.
// Surfaces the CRM sync health card so admins (super_admin /
// administrator / marketing) can see at a glance whether the daily
// scheduledCrmSync (05:00 AEST) actually ran and what it produced.
//
// Routing decision (2026-05-15): a NEW /broker-activity/overview route
// is added rather than changing the subapp's default redirect target.
// Damien's existing muscle memory (default = trail-leakage) is
// preserved — the overview is a discoverable but non-disruptive
// addition. PR #748 (prod merge today) already nailed down the
// trail-leakage-as-default UX; we don't churn that on day one.

import React from 'react';
import { useOutletContext } from 'react-router-dom';
import { Activity } from 'lucide-react';
import { PageHeader } from '../../components/common/PageHeader';
import CrmSyncStatusCard from '../../components/broker-activity/CrmSyncStatusCard';

type OutletCtx = {
  userData?: { companyId?: string; role?: string };
};

const OverviewPage: React.FC = () => {
  const { userData } = useOutletContext<OutletCtx>();
  const companyId = userData?.companyId;
  // Marketing is denied Reports → Trail Leakage and the CRM connection
  // screens, so drop those references from the copy (marketing RBAC §6).
  const isMarketing = userData?.role === 'marketing';

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Activity}
        title="Team Activity - Overview"
        subtitle={
          isMarketing
            ? 'Daily MyCRM sync health and the data foundations behind every other page in this subapp.'
            : 'Daily MyCRM sync health, trail-leakage detection coverage, and the data foundations behind every other page in this subapp.'
        }
      />

      <CrmSyncStatusCard companyId={companyId} role={userData?.role} />

      {/* Future home-page slots — keep stubs minimal to avoid cross-agent
          conflicts with Agent 4 (Weekly Funnel) and Agent 2 (Trail Leakage
          UI explainer). Those agents will add their own contextual cards
          on their respective pages. The overview here is intentionally
          narrow to CRM-sync health on day 1. */}
      <div className="rounded-lg border border-gray-700 bg-gray-800/40 p-4 text-sm text-gray-400">
        {isMarketing ? (
          <>
            Looking for the pipeline funnel? Use the Team Activity tabs in the
            top bar; those pages each have their own deep-dive views. This
            overview focuses on the CRM data pipeline that feeds them.
          </>
        ) : (
          <>
            Looking for the pipeline funnel or the broker scorecard? Use the
            Team Activity tabs in the top bar; those pages each have their own
            deep-dive views. Trail-leakage alerts now live under Reports &rarr;
            Trail Leakage. This overview focuses on the CRM data pipeline that
            feeds them.
          </>
        )}
      </div>
    </div>
  );
};

export default OverviewPage;
